/**
 * Labstep
 *
 * @module models/order-request
 * @desc Typescript export class for OrderRequest
 */

import { Type } from 'class-transformer';
import type { PermissionActions } from 'labstep-web/models';
import { Entity } from 'labstep-web/models/entity.model';
import { EntityUser } from 'labstep-web/models/entity-user.model';
import { Group } from 'labstep-web/models/group.model';
import { Log } from 'labstep-web/models/log.model';
import { Metadata } from 'labstep-web/models/metadata';
import { MetadataType } from 'labstep-web/models/metadata/types';
import { MetadataThread } from 'labstep-web/models/metadata-thread.model';
import { PermaLink } from 'labstep-web/models/perma-link.model';
import { PurchaseOrder } from 'labstep-web/models/purchase-order.model';
import { Resource } from 'labstep-web/models/resource.model';
import { ShareLink } from 'labstep-web/models/share-link.model';
import { Thread } from 'labstep-web/models/thread.model';
import { User } from 'labstep-web/models/user.model';
import {
  type Currency,
  CurrencyService,
} from 'labstep-web/services/currency.service';
import { getHumanReadableEntityName } from 'labstep-web/services/i18n.service';
import { pluralizeWithCheck } from 'labstep-web/services/utils.service';

export enum OrderRequestStatus {
  new = 'new',
  approved = 'approved',
  ordered = 'ordered',
  back_ordered = 'back_ordered',
  received = 'received',
  partially_received = 'partially_received',
  cancelled = 'cancelled',
}

export const OrderRequestStatusValues: Record<string, string> = {
  new: 'Requested',
  approved: 'Approved',
  ordered: 'Ordered',
  back_ordered: 'Back Ordered',
  partially_received: 'Partially Received',
  received: 'Received',
  cancelled: 'Cancelled',
};

export class OrderRequest extends Entity {
  static readonly entityName = 'order_request' as const;

  get entityName(): typeof OrderRequest.entityName {
    return OrderRequest.entityName;
  }

  constructor(data: Partial<OrderRequest> = {}) {
    super();
    Object.assign(this, data);
  }

  static get defaultMetadatas(): Metadata[] {
    return [
      new Metadata({
        label: 'Expected Arrival Date',
        type: MetadataType.date,
        is_required: false,
      }),
    ];
  }

  // Flag used to keep track of whether we display the action in ag grid cell to import items
  // Check shouldImportResourceItems to see when we set it to true
  is_import_resource_items_displayed: boolean = false;

  id!: number;

  name!: string;

  is_urgent!: boolean;

  is_template!: boolean;

  entity_users_count!: number;

  resource_item_count!: number;

  currency!: Currency;

  resource_count!: number;

  status!: OrderRequestStatus;

  quantity!: number;

  amount!: number;

  locked_at!: string | null;

  allowed_actions!: PermissionActions[];

  @Type(() => Group)
  owner!: Group;

  @Type(() => User)
  author!: User;

  @Type(() => Thread)
  thread!: Thread;

  @Type(() => OrderRequest)
  default_order_request_template!: OrderRequest | null;

  @Type(() => OrderRequest)
  template!: OrderRequest | null;

  @Type(() => Resource)
  resource!: Resource;

  @Type(() => PurchaseOrder)
  purchase_order!: PurchaseOrder;

  @Type(() => ShareLink)
  share_link!: ShareLink;

  @Type(() => PermaLink)
  perma_link!: PermaLink;

  @Type(() => MetadataThread)
  metadata_thread!: MetadataThread;

  @Type(() => EntityUser)
  entity_users_preview!: EntityUser[];

  @Type(() => Log)
  locked_log!: Log | null;

  @Type(() => Log)
  ordered_log!: Log | null;

  @Type(() => Log)
  approved_log!: Log | null;

  @Type(() => Log)
  back_ordered_log!: Log | null;

  @Type(() => Log)
  partially_received_log!: Log | null;

  @Type(() => Log)
  received_log!: Log | null;

  @Type(() => Log)
  cancelled_log!: Log | null;

  get printAmount(): string {
    return CurrencyService.print(this.amount, this.currency);
  }

  get printQuantity(): string {
    return `${this.quantity} ${pluralizeWithCheck(
      'item',
      this.quantity,
    )}`;
  }

  get printStatus(): string {
    return OrderRequestStatusValues[this.status];
  }

  get canSetPurchaseOrder(): boolean {
    return !(
      this.purchase_order &&
      !this.purchase_order.canAddOrRemoveOrderRequest
    );
  }

  get lastDateAndUser(): { date: string; user: User } | null {
    if (this.status === OrderRequestStatus.new) {
      return { date: this.created_at, user: this.author };
    }
    const log: Log | null = this[`${this.status}_log`];
    if (log) {
      return { date: log.created_at, user: log.author };
    }
    return null;
  }

  public getStatusLog(status: OrderRequestStatus) {
    if (status === OrderRequestStatus.new) {
      return { created_at: this.created_at, author: this.author };
    }
    return this[`${status}_log`];
  }

  get remainingItemsCount(): number {
    return this.quantity - this.resource_item_count;
  }

  get hasItemsRemaining(): boolean {
    return this.resource_item_count < this.quantity;
  }

  public get nonDeletedTemplate(): OrderRequest | null {
    if (this.template && !this.template.deleted_at) {
      return this.template;
    }
    return null;
  }

  /**
   * Checks if the resource has any metadatas that are required
   * but do not have a value.
   */
  public get requiredMetadatasWithoutValue(): Metadata[] {
    const requiredMetadatas = this.metadata_thread.metadatas.filter(
      (metadata) =>
        metadata.is_required &&
        !metadata.template_id &&
        !metadata.hasValue,
    );

    if (this.nonDeletedTemplate) {
      requiredMetadatas.push(
        ...this.nonDeletedTemplate.metadata_thread.metadatas.filter(
          (metadata) =>
            metadata.is_required &&
            !this.metadata_thread.metadatas.find(
              (m) => m.template_id === metadata.id,
            )?.hasValue,
        ),
      );
    }
    return requiredMetadatas;
  }

  public shouldImportResourceItems(
    status: OrderRequestStatus,
  ): boolean {
    return (
      status !== this.status &&
      [
        OrderRequestStatus.partially_received,
        OrderRequestStatus.received,
      ].includes(status) &&
      this.hasItemsRemaining
    );
  }

  static getHumanReadableEntityName(
    plural?: boolean,
    capitalized?: boolean,
  ): string {
    return getHumanReadableEntityName(
      this.entityName,
      plural,
      capitalized,
    );
  }
}
